import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { FeatureFlagsService } from './feature-flags.service';

/**
 * @description factory that returns a `CanActivateFn` that allows
 * or blocks access to a route if a given feature flag is set.
 * @param {string} features the feature flag to check for
 * @param {boolean} [canActivateIf=true] the route can be activated when
 * the feature flag matches this value. Defaults to `true`
 * @param {any[]} [redirectTo] an array of URL fragments from which
 * the redirect route URL tree is constructed if access is denied.
 * @see https://angular.dev/api/router/Router#createUrlTree
 * @returns {boolean | RedirectCommand} `true` when the route can be
 * activated. If not either `false` will be returned, or a `RedirectCommand`
 * constructed from `redirectTo`.
 * @see https://angular.dev/api/router/RedirectCommand#
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export const featureFlagGuard =
    (features: string | string[], canActivateIf: boolean = true, redirectTo?: any[]): CanActivateFn =>
    () => {
        const featureFlagService = inject(FeatureFlagsService);
        const router = inject(Router);

        if (!Array.isArray(features)) {
            features = [features];
        }

        if (features.some((feature) => featureFlagService.hasFeature(feature) === canActivateIf)) {
            // If any of the feature flags matches the expected value, allow activation
            return true;
        }

        if (redirectTo != null) {
            return new RedirectCommand(router.createUrlTree(redirectTo));
        }

        return false;
    };
